import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { ConferenceTvSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/** Screen-space pin (percent of viewport) for the "switch on the TV" hotspot. */
export type TvPin = { xPct: number; yPct: number; visible: boolean; distance: number };

/**
 * The wall-mounted conference-room TV. It reports its screen position each frame
 * (so App can enable a "Switch on the TV" control when the learner is near and
 * looking at it) and, once switched `on`, lights an emissive image plane exactly
 * over the panel so the display "powers on" with the supplied picture.
 */
export function ConferenceTv({
  spec,
  on,
  onPin,
}: {
  spec: ConferenceTvSpec;
  on: boolean;
  onPin: (pin: TvPin) => void;
}) {
  const { camera } = useThree();
  const anchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<TvPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const tex = useMemo(() => {
    const t = new THREE.TextureLoader().load(spec.imageUrl);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    return t;
  }, [spec.imageUrl]);
  useEffect(() => () => tex.dispose(), [tex]);

  const yaw = (spec.rotationDeg ?? 90) * DEG;

  useFrame(() => {
    const a = anchor.current;
    if (!a) return;
    a.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: TvPin = { xPct, yPct, visible: inFront && onScreen && distance < 7, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __tvPin?: TvPin }).__tvPin = pin;
    }
  });

  return (
    <group ref={anchor} position={spec.screenCenter} rotation={[0, yaw, 0]}>
      {on && (
        <mesh position={[0, 0, 0.02]}>
          <planeGeometry args={[spec.width, spec.height]} />
          <meshStandardMaterial
            map={tex}
            emissive="#ffffff"
            emissiveMap={tex}
            emissiveIntensity={1.0}
            toneMapped={false}
          />
        </mesh>
      )}
    </group>
  );
}
